import React, { useEffect, useId, useRef } from 'react';
import Box from '@mui/joy/Box';

const SIZE_PX = { sm: 16, md: 18, lg: 22 };

/**
 * Native `<input type="checkbox">` + optional label — reliable in RTL (no Joy/MUI overlay issues).
 * API aligned with Joy Checkbox for drop-in replacement: `label`, `checked`, `onChange`, `size`, `sx`.
 */
export default function NativeCheckbox({
  checked,
  defaultChecked,
  onChange,
  disabled,
  label,
  name,
  id,
  required,
  size = 'md',
  indeterminate = false,
  sx,
  slotProps: _slotProps,
  className,
  style,
  ...rest
}) {
  const inputRef = useRef(null);
  const generatedId = useId();
  const inputId = id != null ? id : `ncb-${generatedId}`;
  const px = SIZE_PX[size] ?? SIZE_PX.md;

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.indeterminate = Boolean(indeterminate);
    }
  }, [indeterminate]);

  const controlled = checked !== undefined;
  const inputStyle = {
    width: px,
    height: px,
    margin: 0,
    flexShrink: 0,
    cursor: disabled ? 'not-allowed' : 'pointer',
    accentColor: 'var(--joy-palette-primary-500, #5f09c2)',
    verticalAlign: 'middle',
    ...style,
  };

  const inputProps = {
    ref: inputRef,
    id: inputId,
    type: 'checkbox',
    name,
    required,
    disabled,
    onChange,
    className,
    style: inputStyle,
    ...(controlled ? { checked } : { defaultChecked }),
  };

  if (label != null && label !== '') {
    return (
      <Box
        component="label"
        htmlFor={inputId}
        sx={{
          display: 'inline-flex',
          alignItems: 'flex-start',
          gap: '0.5rem',
          cursor: disabled ? 'not-allowed' : 'pointer',
          userSelect: 'none',
          ...sx,
        }}
        {...rest}
      >
        <input {...inputProps} />
        <Box component="span" sx={{ flex: 1, minWidth: 0, lineHeight: 1.5, pt: '0.1em' }}>
          {label}
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        verticalAlign: 'middle',
        ...sx,
      }}
      {...rest}
    >
      <input {...inputProps} />
    </Box>
  );
}
